import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 토큰 갱신 중복 방지
let isRefreshing = false;
let isRedirecting = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
};

const clearTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('refreshToken');
};

const handleSessionExpired = () => {
  isRedirecting = true;
  isRefreshing = false;
  processQueue(new Error('Session expired'), null);
  clearTokens();
  window.location.href = '/login';
};

// 토큰 갱신이 불필요한 공개 API
const PUBLIC_URLS = ['/auth/login', '/auth/signup', '/auth/refresh',
  '/auth/naver-login', '/auth/google-login', '/auth/kakao-login',
  '/auth/oauth-config', '/system-config'];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 리다이렉트 중이면 모든 에러 무시 (무한 루프 차단)
    if (isRedirecting) return new Promise(() => {});

    const originalRequest = error.config;
    const url = originalRequest?.url || '';

    // 공개 API는 토큰 갱신 시도 안 함
    if (PUBLIC_URLS.some(u => url.includes(u))) {
      return Promise.reject(error);
    }

    // 401이고 아직 재시도 안 한 경우 → 토큰 갱신 시도
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

      if (!refreshToken) {
        handleSessionExpired();
        return new Promise(() => {});
      }

      if (isRefreshing) {
        // 이미 갱신 중이면 큐에 대기
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(() => new Promise(() => {}));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(
          `${api.defaults.baseURL}/auth/refresh`,
          { refreshToken }
        );
        const { token, refreshToken: newRefreshToken } = res.data;
        const tokenStore = localStorage.getItem('tokenStorage') === 'local' ? localStorage : sessionStorage;

        tokenStore.setItem('token', token);
        tokenStore.setItem('refreshToken', newRefreshToken);

        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        originalRequest.headers.Authorization = `Bearer ${token}`;

        isRefreshing = false;
        processQueue(null, token);
        return api(originalRequest);
      } catch (refreshError) {
        handleSessionExpired();
        return new Promise(() => {});
      }
    }

    return Promise.reject(error);
  }
);

export default api;
